import { GuestInvitations } from '@/components/course-participation'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, isAfter, parseISO, startOfToday } from 'date-fns'
import { de } from 'date-fns/locale'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
    SessionDetailDialog,
    type SessionDetailData,
} from '@/components/session-detail-dialog'
import { cancelMemberBooking, getMemberBookings, getResourceCategories, getResources } from '@/lib/api'
import { getGuestInvitations } from '@/lib/credits-api'
import { ResourceSelectionHeader } from '@/components/resource-selection-header'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import type { Booking } from '@/types/domain'

function bookingToSessionDetail(booking: Booking): SessionDetailData {
    const cs = booking.course_sessions
    const availableSpots = cs ? cs.max_participants - cs.confirmed_count : null
    return {
    bookingId: booking.id,
    sessionId: booking.session_id,
        courseName: cs?.courses?.name ?? 'Kurs',
        courseDescription: cs?.courses?.description ?? null,
        startsAt: cs?.starts_at ?? '',
        endsAt: cs?.ends_at ?? '',
        trainerName: cs?.trainer_name ?? null,
        trainerId: cs?.trainer_id ?? null,
        availableSpots,
        maxParticipants: cs?.max_participants ?? null,
        status: booking.status === 'confirmed' ? 'confirmed'
            : 'cancelled',
        registrationUntil: cs?.registration_until ?? null,
        cancellationUntil: cs?.cancellation_until ?? null,
        minParticipants: cs?.min_participants ?? null,
        confirmedCount: cs?.confirmed_count ?? null,
        creditsRequired: booking.credits_required ?? cs?.courses?.credits_per_session ?? null,
        subscriptionCreditsUsed: booking.subscription_credits_used ?? null,
        balanceCreditsUsed: booking.balance_credits_used ?? null,
    }
}

function BookingCard({ booking, onClick, onCancel }: { booking: Booking; onClick: () => void; onCancel?: () => void }) {
    const session = booking.course_sessions
    const startsAt = session?.starts_at ? parseISO(session.starts_at) : null
    const endsAt = session?.ends_at ? parseISO(session.ends_at) : null
    const hasValidSchedule =
        startsAt !== null &&
        endsAt !== null &&
        !Number.isNaN(startsAt.getTime()) &&
        !Number.isNaN(endsAt.getTime())
    const statusLabel =
        booking.status === 'confirmed'
            ? 'Bestätigt'
            : booking.status === 'cancelled'
                ? 'Storniert'
                : 'Bestätigt'
    const statusVariant =
        booking.status === 'confirmed'
            ? 'default'
            : booking.status === 'cancelled'
                ? 'destructive'
                : ('default' as const)

    return (
        <Card
            className="border-border/80 transition hover:shadow-md cursor-pointer"
            onClick={onClick}
        >
            <CardContent className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between">
                <button type="button" className="flex-1 text-left cursor-pointer" onClick={onClick} aria-label={`Kursdetails: ${session?.courses?.name ?? 'Kurs'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{session?.courses?.name ?? 'Kurs'}</h3>
                        <Badge variant={statusVariant}>{statusLabel}</Badge>
                    </div>
                    {hasValidSchedule ? (
                        <p className="text-sm text-muted-foreground">
                            {format(startsAt, "EEEE, dd. MMMM yyyy 'um' HH:mm", {
                                locale: de,
                            })}{' '}
                            – {format(endsAt, 'HH:mm')}
                        </p>
                    ) : session ? (
                        <p className="text-sm text-muted-foreground">
                            Termininformationen nicht verfügbar
                        </p>
                    ) : null}
                    {session?.trainer_name && (
                        <p className="text-xs text-muted-foreground mt-1">
                            Trainer: {session.trainer_name}
                        </p>
                    )}
                </button>
                {onCancel && (
                    <Button variant="outline" size="sm" onClick={(event) => { event.stopPropagation(); onCancel() }}>
                        Stornieren
                    </Button>
                )}
            </CardContent>
        </Card>
    )
}

type MemberCoursesTab = 'upcoming' | 'past' | 'guest'

export function MemberCoursesPage() {
    const queryClient = useQueryClient()
    const [tab, setTab] = useState<MemberCoursesTab>('upcoming')
    const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null)
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
    const [cancellingBooking, setCancellingBooking] = useState<Booking | null>(null)

    const bookingsQuery = useQuery({
        queryKey: queryKeys.memberBookings,
        queryFn: () => getMemberBookings({}),
        retry: false,
    })

    const guestInvitationsQuery = useQuery({
        queryKey: queryKeys.guestInvitationsWithPast,
        queryFn: () => getGuestInvitations(true),
    })

    const resourceCategoriesQuery = useQuery({
        queryKey: queryKeys.resourceCategories,
        queryFn: getResourceCategories,
    })

    const resourcesQuery = useQuery({
        queryKey: queryKeys.resources,
        queryFn: () => getResources(),
    })

    const cancelMutation = useMutation({
        mutationFn: cancelMemberBooking,
        onSuccess: () => {
            toast.success('Buchung erfolgreich storniert.')
            setSelectedBooking(null)
            setCancellingBooking(null)
            queryClient.invalidateQueries({ queryKey: queryKeys.memberBookings })
            queryClient.invalidateQueries({ queryKey: queryKeys.publicSessionsBase })
            queryClient.invalidateQueries({ queryKey: queryKeys.userCreditBalancesBase })
            queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
            queryClient.invalidateQueries({ queryKey: queryKeys.memberBookingsUpcoming })
        },
        onError: (error) => {
            toast.error(getErrorMessage(error))
        },
    })

    const bookings = useMemo(() => bookingsQuery.data ?? [], [bookingsQuery.data])
    const resourceCategories = useMemo(() => resourceCategoriesQuery.data ?? [], [resourceCategoriesQuery.data])
    const resources = useMemo(() => resourcesQuery.data ?? [], [resourcesQuery.data])
    const guestInvitations = guestInvitationsQuery.data ?? []
    const pendingInvitationCount = guestInvitations.filter((i) => i.status === 'pending').length

    const categoriesWithResources = useMemo(() =>
        resourceCategories.map(cat => ({
            ...cat,
            resources: resources.filter(r => r.category_id === cat.id),
        })),
        [resourceCategories, resources]
    )

    const { upcoming, past } = useMemo(() => {
        const today = startOfToday()
        const up: Booking[] = []
        const pa: Booking[] = []

        bookings.forEach((b) => {
            if (b.status === 'cancelled') {
                pa.push(b)
                return
            }
            const startsAt = b.course_sessions?.starts_at
            if (startsAt && isAfter(parseISO(startsAt), today)) {
                up.push(b)
            } else {
                pa.push(b)
            }
        })

        return {
            upcoming: up.sort((a, b) => {
                const aDate = a.course_sessions?.starts_at ?? ''
                const bDate = b.course_sessions?.starts_at ?? ''
                return aDate.localeCompare(bDate)
            }),
            past: pa.sort((a, b) => {
                const aDate = a.course_sessions?.starts_at ?? ''
                const bDate = b.course_sessions?.starts_at ?? ''
                return bDate.localeCompare(aDate)
            }),
        }
    }, [bookings])

    const filteredUpcoming = useMemo(() => {
        if (!selectedResourceId) return upcoming
        return upcoming.filter(b => b.course_sessions?.resource_id === selectedResourceId)
    }, [upcoming, selectedResourceId])

    const filteredPast = useMemo(() => {
        if (!selectedResourceId) return past
        return past.filter(b => b.course_sessions?.resource_id === selectedResourceId)
    }, [past, selectedResourceId])

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Meine Kurse</h1>
                <p className="text-sm text-muted-foreground">
                    Deine eigenen Buchungen und Gastteilnahmen.
                </p>
            </div>

            <Tabs value={tab} onValueChange={(v) => setTab(v as MemberCoursesTab)}>
                <TabsList className="grid w-full grid-cols-3 max-w-[450px]">
                    <TabsTrigger value="upcoming">
                        Zukünftige ({upcoming.length})
                    </TabsTrigger>
                    <TabsTrigger value="past">
                        Vergangene ({filteredPast.length})
                    </TabsTrigger>
                    <TabsTrigger value="guest" className="gap-1.5">
                        Als Gast ({guestInvitations.length})
                        {pendingInvitationCount > 0 && (
                            <span
                                className="size-2 rounded-full bg-primary"
                                aria-label={`${pendingInvitationCount} offene Einladungen`}
                            />
                        )}
                    </TabsTrigger>
                </TabsList>

                {tab !== 'guest' && (
                    <ResourceSelectionHeader
                        categories={categoriesWithResources}
                        selectedResourceId={selectedResourceId}
                        onSelect={setSelectedResourceId}
                        className="mt-6"
                    />
                )}

                <TabsContent value="upcoming" className="mt-4">
                    {bookingsQuery.isLoading ? (
                        <div className="space-y-3">
                            <Skeleton className="h-20 w-full" />
                            <Skeleton className="h-20 w-full" />
                        </div>
                    ) : upcoming.length === 0 ? (
                        <Card>
                            <CardContent className="py-8 text-center text-sm text-muted-foreground">
                                Du hast keine eigenen kommenden Buchungen.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {filteredUpcoming.map((booking) => (
                                <BookingCard
                                    key={booking.id}
                                    booking={booking}
                                    onClick={() => setSelectedBooking(booking)}
                                    onCancel={booking.status === 'confirmed' &&
                                        new Date(booking.course_sessions?.starts_at ?? '') > new Date() &&
                                        (!booking.course_sessions?.cancellation_until || new Date(booking.course_sessions.cancellation_until) > new Date())
                                        ? () => setCancellingBooking(booking) : undefined}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="past" className="mt-4">
                    {bookingsQuery.isLoading ? (
                        <div className="space-y-3">
                            <Skeleton className="h-20 w-full" />
                            <Skeleton className="h-20 w-full" />
                        </div>
                    ) : past.length === 0 ? (
                        <Card>
                            <CardContent className="py-8 text-center text-sm text-muted-foreground">
                                Noch keine vergangenen Kurse.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {filteredPast.map((booking) => (
                                <BookingCard
                                    key={booking.id}
                                    booking={booking}
                                    onClick={() => setSelectedBooking(booking)}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="guest" className="mt-4">
                    {guestInvitationsQuery.isLoading ? (
                        <div className="space-y-3">
                            <Skeleton className="h-20 w-full" />
                        </div>
                    ) : (
                        <GuestInvitations />
                    )}
                </TabsContent>
            </Tabs>

            <SessionDetailDialog
                open={selectedBooking !== null}
                onOpenChange={(open) => {
                    if (!open) setSelectedBooking(null)
                }}
                session={selectedBooking ? bookingToSessionDetail(selectedBooking) : null}
                title="Kursdetails"
                description="Informationen zu deinem gebuchten Kurs."
            />
            <SessionDetailDialog
                open={cancellingBooking !== null}
                onOpenChange={(open) => {
                    if (!open && !cancelMutation.isPending) setCancellingBooking(null)
                }}
                session={cancellingBooking ? bookingToSessionDetail(cancellingBooking) : null}
                title="Buchungsdetails"
                description="Hier kannst du deine Buchung und Gäste verwalten."
                onCancel={cancellingBooking ? () => cancelMutation.mutate(cancellingBooking.id) : undefined}
                cancelPending={cancelMutation.isPending}
            />
        </div>
    )
}
